import * as RadixTabs from '@radix-ui/react-tabs';
import { cn } from '@/lib/cn';

/**
 * The portal's segmented control: a light blue tray with 6px radius, the active
 * segment white with navy text. Used in the header for Settings and Admin, and
 * inside a card wherever a page has two views.
 */
const trayClass =
  'inline-flex items-center gap-1 rounded-lg border border-[color:var(--ts-border-soft)] bg-[color:var(--ts-tray)] p-1';

const segmentClass =
  'inline-flex h-8 items-center justify-center rounded-md px-4 text-base font-medium text-gray-1 ' +
  'transition-[background-color,color,box-shadow] duration-150 ease-out ' +
  'hover:bg-[color:var(--ts-surface)]/60 hover:text-primary';

const activeSegmentClass = 'bg-surface text-primary shadow-card';

export const Tabs = RadixTabs.Root;
export const TabsContent = RadixTabs.Content;

export function TabsList({ className, ...props }: React.ComponentPropsWithoutRef<typeof RadixTabs.List>) {
  return <RadixTabs.List className={cn(trayClass, className)} {...props} />;
}

export function TabsTrigger({ className, ...props }: React.ComponentPropsWithoutRef<typeof RadixTabs.Trigger>) {
  return (
    <RadixTabs.Trigger
      className={cn(
        segmentClass,
        'data-[state=active]:bg-surface data-[state=active]:text-primary',
        'data-[state=active]:shadow-card',
        className,
      )}
      {...props}
    />
  );
}

export interface SegmentedItem {
  value: string;
  label: string;
}

/** Standalone segmented control for the header, where the value is the URL. */
export function Segmented({
  value,
  onValueChange,
  items,
  ariaLabel,
  className,
}: {
  value: string;
  onValueChange: (value: string) => void;
  items: SegmentedItem[];
  ariaLabel: string;
  className?: string;
}) {
  return (
    <div role="tablist" aria-label={ariaLabel} className={cn(trayClass, className)}>
      {items.map((item) => {
        const active = item.value === value;
        return (
          <button
            key={item.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onValueChange(item.value)}
            className={cn(segmentClass, active && activeSegmentClass)}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}
